import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-brain-line',
    title: 'Mobiliário neuro-inclusivo certificado',
    description: 'Bordas arredondadas, cores neutras e materiais que não geram ruído visual ou tátil. Cada peça projetada para não interferir no protocolo de avaliação e terapia.',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Segurança estrutural para uso intenso',
    description: 'Estruturas com base de aço, cantos arredondados e acabamentos atóxicos. Mobiliário que protege a criança durante as sessões e resiste ao uso diário das clínicas.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito da sala de terapia',
    description: 'Antes de fabricar, você aprova o layout completo da sala em 3D. Posicionamento de móveis, circulação e organização do espaço pensados junto com sua equipe clínica.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipe própria de montagem',
    description: 'Instalação feita pela nossa equipe, no horário que sua clínica preferir. Sem terceiros, sem imprevistos. Você recebe o ambiente pronto para atender no dia seguinte.',
  },
];

const blocks = [
  {
    title: 'Mobiliário que Auxilia na Terapia e Garante a Segurança das Crianças',
    text: [
      'O ambiente físico é parte do protocolo terapêutico. Crianças com TEA processam o espaço de forma diferente: cores muito saturadas aumentam a sobrecarga sensorial, texturas ásperas geram desconforto tátil, e cantos pontiagudos representam risco real durante episódios de agitação. Nosso mobiliário neuro-inclusivo elimina esses fatores de estresse antes que a sessão comece.',
      'Utilizamos paletas de cores neutras e terrosas (verde sage, bege quente, cinza suave), madeira natural com acabamento lixado e encerado sem verniz brilhante, e formas arredondadas em todos os cantos e bordas. O resultado é um ambiente que comunica segurança e previsibilidade — dois elementos fundamentais para que a criança com TEA se regule e engaje na terapia.',
    ],
    img: '/media/ia/0301.webp',
    reverse: false,
  },
  {
    title: 'Salas de Avaliação e Atendimento ABA Projetadas para o Protocolo',
    text: [
      'Clínicas ABA têm necessidades específicas que móveis genéricos não atendem. A mesa de avaliação precisa ter altura regulável para acomodar crianças de diferentes idades e tamanhos. A cadeira do terapeuta precisa de mobilidade para acompanhar a criança sem interromper a sessão. E o espaço precisa de divisórias acústicas que garantam sigilo sem criar isolamento visual total.',
      'Oferecemos mesas de avaliação com regulagem de altura elétrica (60–90cm), cadeiras ergonômicas com rodízios silenciosos para o terapeuta, poltronas de balanço terapêutico com base estável para regulação sensorial, divisórias acústicas modulares que criam salas privativas dentro de um espaço maior, e armários organizadores com pictogramas visuais para facilitar a rotina da criança.',
    ],
    img: '/media/ia/0302.webp',
    reverse: true,
  },
  {
    title: 'Cantinho de Regulação Sensorial e Sala de Espera Acolhedora',
    text: [
      'O cantinho de regulação é o espaço onde a criança se prepara para a sessão ou se acalma após uma crise. Ele precisa de poltrona de balanço com assento acolchoado, pufes de diferentes texturas, iluminação LED regulável com temperatura de cor quente (2700K), e divisória acústica que reduz o estímulo auditivo externo sem isolar completamente.',
      'A sala de espera também importa. Pais e responsáveis que esperam precisam de cadeiras confortáveis, mesa de apoio para notebook e café, e um ambiente que transmita profissionalismo e cuidado. Projetamos tudo de forma integrada — da recepção ao consultório — para que a experiência da família seja positiva do início ao fim.',
    ],
    img: '/media/ia/0303.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0304.webp',
    alt: 'Poltrona de balanço terapêutica',
  },
  {
    src: '/media/ia/0305.webp',
    alt: 'Mesa de avaliação regulável',
  },
  {
    src: '/media/ia/0306.webp',
    alt: 'Divisórias acústicas modulares',
  },
  {
    src: '/media/ia/0307.webp',
    alt: 'Sala de espera para clínica ABA',
  },
];

export default function AutismoPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário Neuro-Inclusivo para Clínicas ABA e Autismo | Caruaru PE',
    description: 'Mobiliário neuro-inclusivo para clínicas ABA, TEA e neurodesenvolvimento. Bordas arredondadas, cores neutras, projeto 3D gratuito. Caruaru e Agreste PE.',
    keywords: 'mobiliário ABA autismo Recife, clínica neurodesenvolvimento Petrolina, mobiliário TEA Garanhuns, sala terapia autismo Arcoverde, móveis clínica ABA Sertão PE, poltrona terapia autismo Pernambuco, mobiliário neuro inclusivo Agreste, mobiliário ABA Nordeste, mesa avaliação autismo Recife, poltrona balanço terapia Petrolina, divisória acústica clínica Garanhuns, mobiliário autismo Caruaru, mobiliário ABA Bezerros',
    path: '/autismo',
  });

  useSchemaLD({
    id: 'schema-autismo',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário Neuro-Inclusivo para Clínicas ABA e Autismo | Caruaru PE',
      description: 'Mobiliário neuro-inclusivo para clínicas ABA, TEA e neurodesenvolvimento. Bordas arredondadas, cores neutras, projeto 3D gratuito.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/autismo`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Clínicas ABA e Autismo',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0308.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Neurodesenvolvimento · Clínicas ABA · Autismo
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Mobiliário que auxilia na terapia e garante a segurança das crianças.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Bordas arredondadas, cores neutras e materiais que não geram ruído visual ou tátil. Ambientes projetados para não interferir no protocolo de avaliação — e potencializar os resultados da terapia.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20ABA%20e%20Autismo%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que clínicas de neurodesenvolvimento escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Design Terapêutico</span> em Cada Detalhe
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20ABA%20e%20Autismo%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Projeto 3D completo da sua clínica, antes de comprar qualquer móvel.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Nossa equipe visita sua clínica, entende o protocolo de atendimento e entrega um projeto 3D em até 48h. Você aprova cada detalhe antes de fabricarmos.
          </p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20ABA%20e%20Autismo%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">
              Ambientes <span className="text-[#E8610A]">Neuro-Inclusivos</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20ABA%20e%20Autismo%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" />
            </a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" />
            </a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Cl%C3%ADnicas%20ABA%20e%20Autismo%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}